import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import {
  PAGE_SIZE,
  TablePagination,
} from "../components/TablePagination";
import {
  addUserSnippet,
  addUserSnippetsBulk,
  deleteAllUserSnippets,
  deleteSnippet,
  fetchUserSnippets,
  updateUserSnippet,
} from "../api/snippets";
import { MEDICAL_SPECIALTIES } from "../constants/profileOptions";
import { useLanguage } from "../i18n/LanguageContext";
import { t } from "../i18n";
import { specialtyLabel } from "./helpers";

const DEFAULT_SPECIALTY = "General Medicine";

export const SnippetsPage = () => {
  const { portal, language } = useLanguage();
  const copy = portal.snippets;
  const common = portal.common;
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [text, setText] = useState("");
  const [specialty, setSpecialty] = useState(DEFAULT_SPECIALTY);
  const [editingId, setEditingId] = useState(null);
  const [editingTimesUsed, setEditingTimesUsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingFiltered, setDeletingFiltered] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await fetchUserSnippets();
      setSnippets(Array.isArray(next) ? next : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setSnippets([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const specialtyFiltered = useMemo(() => {
    if (!specialtyFilter) return snippets;
    return snippets.filter(
      (snippet) => (snippet.specialty || "") === specialtyFilter
    );
  }, [snippets, specialtyFilter]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const ordered = [...specialtyFiltered].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
    if (!query) return ordered;
    return ordered.filter((snippet) => {
      const snippetText = (snippet.text || "").toLowerCase();
      const snippetSpecialty = (snippet.specialty || "").toLowerCase();
      return (
        snippetText.includes(query) || snippetSpecialty.includes(query)
      );
    });
  }, [specialtyFiltered, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search, specialtyFilter]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const lines = useMemo(
    () =>
      text
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    [text]
  );

  const resetForm = () => {
    setText("");
    setSpecialty(DEFAULT_SPECIALTY);
    setEditingId(null);
    setEditingTimesUsed(0);
  };

  const handleEdit = (snippet) => {
    setEditingId(snippet._id);
    setEditingTimesUsed(snippet.times_used ?? 0);
    setText(snippet.text || "");
    const nextSpecialty = snippet.specialty || DEFAULT_SPECIALTY;
    setSpecialty(
      nextSpecialty === "general" ? DEFAULT_SPECIALTY : nextSpecialty
    );
    setSuccess("");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!text.trim()) return;

    const nextSpecialty = specialty.trim() || DEFAULT_SPECIALTY;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (editingId) {
        await updateUserSnippet(editingId, {
          text: text.trim(),
          specialty: nextSpecialty,
          source: "user",
          times_used: editingTimesUsed,
        });
        setSuccess(copy.updated);
      } else if (lines.length === 1) {
        await addUserSnippet({ text: lines[0], specialty: nextSpecialty });
        setSuccess(copy.added);
      } else {
        const result = await addUserSnippetsBulk(text, nextSpecialty);
        const skipped = result.skipped_count || 0;
        const inserted = result.inserted_count || 0;
        const skippedNote =
          skipped > 0 ? t(copy.skippedNote, { count: skipped }) : "";
        setSuccess(
          t(copy.addedBulk, { count: inserted, skipped: skippedNote })
        );
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (snippet) => {
    const confirmed = window.confirm(
      t(copy.confirmDelete, { text: snippet.text })
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    try {
      await deleteSnippet(snippet._id);
      if (editingId === snippet._id) resetForm();
      setSuccess(copy.deleted);
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFailed);
    }
  };

  const handleDeleteFiltered = async () => {
    if (filtered.length === 0 || deletingFiltered || deletingAll) return;
    const confirmed = window.confirm(
      t(copy.confirmDeleteFiltered, { count: filtered.length })
    );
    if (!confirmed) return;

    setDeletingFiltered(true);
    setError("");
    setSuccess("");
    try {
      const toDelete = [...filtered];
      await Promise.all(toDelete.map((snippet) => deleteSnippet(snippet._id)));
      if (editingId && toDelete.some((snippet) => snippet._id === editingId)) {
        resetForm();
      }
      setSuccess(t(copy.deletedFiltered, { count: toDelete.length }));
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFilteredFailed);
      await loadData();
    } finally {
      setDeletingFiltered(false);
    }
  };

  const handleDeleteAll = async () => {
    if (snippets.length === 0 || deletingAll || deletingFiltered) return;
    const confirmed = window.confirm(
      t(copy.confirmDeleteAll, { count: snippets.length })
    );
    if (!confirmed) return;

    setDeletingAll(true);
    setError("");
    setSuccess("");
    try {
      const result = await deleteAllUserSnippets();
      resetForm();
      setSuccess(
        t(copy.deletedAll, { count: result.deleted_count ?? snippets.length })
      );
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteAllFailed);
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <AppShell
      title={copy.title}
      description={copy.description}
      action={
        <Link to="/test" className="app-secondary">
          {common.openTestPage}
        </Link>
      }
    >
      {error && <div className="app-alert">{error}</div>}
      {success && <div className="app-alert success">{success}</div>}

      <section className="app-form-card">
        <h2>{editingId ? copy.editTitle : copy.addTitle}</h2>
        <form onSubmit={handleSubmit} className="app-form">
          <label htmlFor="snippet-text">{copy.textLabel}</label>
          <textarea
            id="snippet-text"
            rows={editingId ? 3 : 5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              editingId ? copy.placeholderEdit : copy.placeholderAdd
            }
            required
            disabled={saving}
          />
          <label htmlFor="snippet-specialty">{copy.specialtyLabel}</label>
          <select
            id="snippet-specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            disabled={saving}
          >
            {MEDICAL_SPECIALTIES.map((option) => (
              <option key={option} value={option}>
                {specialtyLabel(option, language)}
              </option>
            ))}
          </select>
          {!editingId && lines.length > 1 && (
            <p className="app-hint">
              {t(copy.readyToAdd, { count: lines.length })}
            </p>
          )}
          <div className="app-form-actions">
            <button type="submit" className="app-primary" disabled={saving}>
              {saving
                ? common.saving
                : editingId
                  ? copy.update
                  : lines.length > 1
                    ? t(copy.addMany, { count: lines.length })
                    : copy.addOne}
            </button>
            {editingId && (
              <button
                type="button"
                className="app-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                {common.cancel}
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="app-table-card">
        <div className="app-stats">
          <span>
            {common.total}: {snippets.length}
          </span>
        </div>
        <div className="app-toolbar">
          <input
            type="search"
            className="app-search"
            placeholder={copy.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={copy.searchAria}
          />
          <select
            className="app-filter"
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
            aria-label={copy.specialtyFilterAria}
          >
            <option value="">{copy.allSpecialties}</option>
            {MEDICAL_SPECIALTIES.map((option) => (
              <option key={option} value={option}>
                {specialtyLabel(option, language)}
              </option>
            ))}
          </select>
          <span className="app-count">
            {loading
              ? common.loading
              : `${filtered.length} ${common.of} ${snippets.length}`}
          </span>
          {snippets.length > 0 && (
            <div className="app-toolbar-actions">
              {(search.trim() || specialtyFilter) && filtered.length > 0 && (
                <button
                  type="button"
                  className="app-secondary"
                  onClick={handleDeleteFiltered}
                  disabled={
                    deletingFiltered || deletingAll || loading || saving
                  }
                >
                  {deletingFiltered ? common.deleting : common.delete}
                </button>
              )}
              <button
                type="button"
                className="app-secondary"
                onClick={handleDeleteAll}
                disabled={deletingAll || deletingFiltered || loading || saving}
              >
                {deletingAll ? common.deleting : copy.deleteAll}
              </button>
            </div>
          )}
        </div>

        <div className="app-table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th className="app-text-cell">{copy.colText}</th>
                <th className="app-specialty-cell">{copy.colSpecialty}</th>
                <th className="app-actions-cell">{copy.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={3} className="app-empty">
                    {copy.empty}
                  </td>
                </tr>
              )}
              {paged.map((snippet) => (
                <tr key={snippet._id}>
                  <td className="app-text-cell">{snippet.text}</td>
                  <td className="app-specialty-cell">
                    {specialtyLabel(snippet.specialty, language)}
                  </td>
                  <td className="app-actions-cell">
                    <button
                      type="button"
                      className="app-link-btn"
                      onClick={() => handleEdit(snippet)}
                    >
                      {common.edit}
                    </button>
                    <button
                      type="button"
                      className="app-link-btn danger"
                      onClick={() => handleDelete(snippet)}
                    >
                      {common.delete}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <TablePagination
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          labels={{
            previous: common.previous,
            next: common.next,
            pageStatus: common.pageStatus,
            aria: common.paginationAria,
          }}
        />
      </section>
    </AppShell>
  );
};
